import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  addForm,
  deleteForm,
  updateForm,
  type Character,
  type CharacterForm,
  type Characters,
  type DrawPrice,
} from "../api";
import MutationError from "../components/MutationError";
import { useT } from "../i18n";
import PortraitButton from "./PortraitButton";

/**
 * CharacterForms are a character's other shapes (ADR-0159, requirement 0091):
 * Minerva as Mentes, a chieftain forty years younger. Each has a face of its
 * own, so a picture of the form is drawn from it and not from the
 * character's — which is the whole reason a form exists rather than an alias.
 */
export default function CharacterForms({
  bookID,
  character,
  canEdit,
  canDescribe,
  prices,
  onChanged,
}: {
  bookID: string;
  character: Character;
  /** Whether the roster may be written (not a linked book). */
  canEdit: boolean;
  canDescribe: boolean;
  prices: DrawPrice[];
  onChanged: (roster: Characters) => void;
}) {
  const t = useT();
  const [editing, setEditing] = useState<CharacterForm | "new" | null>(null);
  const forms = character.forms ?? [];

  const remove = useMutation({
    mutationFn: (formID: string) => deleteForm(bookID, character.id, formID),
    onSuccess: onChanged,
  });

  if (forms.length === 0 && !canEdit) return null;

  return (
    <div className="mt-2" data-testid="character-forms">
      {forms.length > 0 && (
        <ul className="space-y-2">
          {forms.map((f) => (
            <li key={f.id} data-testid="character-form" className="flex items-start gap-3">
              <PortraitButton
                bookID={bookID}
                character={character}
                form={f}
                canDescribe={canDescribe}
                prices={prices}
                onChanged={onChanged}
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm">
                  <span dir="auto">{t.characters.forms.of(f.name, character.name)}</span>
                </p>
                {f.aliases.length > 0 && (
                  <p className="text-xs text-muted" dir="auto">
                    {t.characters.alsoKnownAs(f.aliases.join("، "))}
                  </p>
                )}
                {f.appearance && (
                  <p className="mt-0.5 text-xs leading-5 text-ink-soft" dir="auto">
                    {f.appearance}
                  </p>
                )}
              </div>
              {canEdit && (
                <div className="flex shrink-0 gap-1.5">
                  <button
                    type="button"
                    data-testid="edit-form"
                    onClick={() => setEditing(f)}
                    className="rounded-control border border-line px-2.5 py-1 text-xs transition-colors hover:bg-sunk"
                  >
                    {t.characters.edit}
                  </button>
                  <button
                    type="button"
                    data-testid="remove-form"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(f.id)}
                    className="rounded-control border border-line px-2.5 py-1 text-xs text-muted transition-colors hover:text-ink disabled:opacity-40"
                  >
                    {t.characters.forms.remove}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <MutationError
        className="mt-1 text-xs"
        testId="remove-form-error"
        error={remove.error}
        fallback={t.characters.forms.saveFailed}
      />
      {canEdit && editing === null && (
        <button
          type="button"
          data-testid="add-form"
          title={t.characters.forms.note}
          onClick={() => setEditing("new")}
          className="mt-1.5 rounded-full border border-line px-2.5 py-0.5 text-xs text-muted transition-colors hover:text-ink"
        >
          {t.characters.forms.add}
        </button>
      )}
      {editing !== null && (
        <FormEditor
          key={editing === "new" ? "new" : editing.id}
          bookID={bookID}
          character={character}
          form={editing === "new" ? undefined : editing}
          onSaved={(roster) => {
            onChanged(roster);
            setEditing(null);
          }}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function FormEditor({
  bookID,
  character,
  form,
  onSaved,
  onCancel,
}: {
  bookID: string;
  character: Character;
  form?: CharacterForm;
  onSaved: (roster: Characters) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const [name, setName] = useState(form?.name ?? "");
  const [aliases, setAliases] = useState((form?.aliases ?? []).join("، "));
  const [appearance, setAppearance] = useState(form?.appearance ?? "");
  const save = useMutation({
    mutationFn: () => {
      const edit = {
        name: name.trim(),
        aliases: aliases
          .split(/[,،]/)
          .map((a) => a.trim())
          .filter(Boolean),
        appearance: appearance.trim(),
      };
      return form
        ? updateForm(bookID, character.id, form.id, edit)
        : addForm(bookID, character.id, edit);
    },
    onSuccess: onSaved,
  });
  const field = "w-full rounded-control border border-line bg-surface px-3 py-2 text-sm";

  return (
    <form
      data-testid="form-editor"
      className="mt-2 space-y-2 rounded-card border border-line p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) save.mutate();
      }}
    >
      <p className="text-xs leading-5 text-muted">{t.characters.forms.note}</p>
      <label className="block text-xs text-muted">
        {t.characters.forms.name}
        <input
          data-testid="form-name"
          dir="auto"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={`mt-1 ${field}`}
        />
      </label>
      <label className="block text-xs text-muted">
        {t.characters.forms.aliases}
        <input
          data-testid="form-aliases"
          dir="auto"
          value={aliases}
          onChange={(e) => setAliases(e.target.value)}
          className={`mt-1 ${field}`}
        />
      </label>
      <label className="block text-xs text-muted">
        {t.characters.forms.appearance}
        <textarea
          data-testid="form-appearance"
          dir="auto"
          rows={3}
          value={appearance}
          onChange={(e) => setAppearance(e.target.value)}
          className={`mt-1 ${field}`}
        />
      </label>
      <MutationError
        className="text-xs"
        testId="form-save-error"
        error={save.error}
        fallback={t.characters.forms.saveFailed}
      />
      <div className="flex gap-2">
        <button
          type="submit"
          data-testid="form-save"
          disabled={!name.trim() || save.isPending}
          className="rounded-control bg-sage px-3 py-1.5 text-xs font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
        >
          {save.isPending ? t.characters.saving : t.characters.forms.save}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-control border border-line px-3 py-1.5 text-xs"
        >
          {t.characters.forms.cancel}
        </button>
      </div>
    </form>
  );
}
